import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Skeleton,
  Typography,
} from '@mui/material'
import { Add as AddIcon } from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import {
  lessonThemeApi,
  programAssignmentsApi,
  type AssignedThemeElement,
  type LessonThemePayload,
} from '@/api/programs'
import { lessonWordsApi } from '@/api/lessonWords'
import { ThemeElementList } from '@/components/programs/ThemeElementList'
import { LessonExercisePanel } from '@/components/exercises/LessonExercisePanel'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

interface LessonProgramBlockProps {
  lessonId: number
  /**
   * Учитель урока. Не то же, что право правки: на отменённом уроке правка
   * закрыта, но учитель обязан видеть плитки класса, а не ученический вид с
   * кнопкой «Практиковаться».
   */
  isLessonTeacher?: boolean
  /** Слова словаря темы ушли в урок — панели слов пора перечитать себя */
  onWordsAdded?: () => void
  /** Обновляется извне при realtime-событии смены темы урока */
  refreshToken?: number
}

/**
 * Блок «Программа» на странице урока.
 *
 * Учитель видит текущую тему с её содержимым и может сменить или снять тему;
 * ученик — то же самое в режиме чтения. Тема приходит вычисленной: сервер
 * ничего не записывает на чтении, поэтому у учителя и ученика она одинаковая
 * ещё до начала урока.
 */
export function LessonProgramBlock({
  lessonId,
  onWordsAdded,
  isLessonTeacher = false,
  refreshToken = 0,
}: LessonProgramBlockProps) {
  const { t } = useTranslation()

  const [payload, setPayload] = useState<LessonThemePayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)

  const load = useCallback(async (background = false) => {
    // Обновление по realtime-событию идёт фоном: иначе любая сдача задания
    // перерисовывала бы весь блок скелетоном прямо во время урока
    if (!background) setLoading(true)
    try {
      setPayload(await lessonThemeApi.get(lessonId))
      setError(null)
      setFailed(false)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
      // Отдельно от error: без этого блок молча исчезал при ошибке загрузки,
      // и учитель не понимал, почему у урока пропала программа.
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [lessonId, t])

  const initialisedRef = useRef(false)

  useEffect(() => {
    void load(initialisedRef.current)
    initialisedRef.current = true
  }, [load, refreshToken])

  /**
   * Добавить тему к уроку. Ответ уже содержит весь набор: повторный GET дал бы
   * второй запрос и второе мигание скелетона поверх realtime-события о той же
   * смене.
   */
  const handleAddTheme = async (themeId: number) => {
    if (!Number.isFinite(themeId) || themeId <= 0) return

    setSaving(true)
    try {
      setPayload(await lessonThemeApi.addTheme(lessonId, themeId))
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveTheme = async (themeId: number) => {
    setSaving(true)
    try {
      setPayload(await lessonThemeApi.removeTheme(lessonId, themeId))
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  /** Снять сразу весь набор — когда тем несколько и убирать их по одной долго */
  const handleDetach = async () => {
    setSaving(true)
    try {
      await lessonThemeApi.detach(lessonId)
      setPayload((current) => (current ? { ...current, theme: null, themes: [] } : current))
      setError(null)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  /**
   * Отметка блока пройденным — действие учителя: у ученика прав на неё нет.
   */
  const handleToggleElement = async (element: AssignedThemeElement, completed: boolean) => {
    setSaving(true)
    try {
      await programAssignmentsApi.completeElement(element.id, completed)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  /**
   * Словарь темы — в слова урока. Одним запросом: сервер сам разложит слова по
   * составу урока и не размножит их копиями.
   */
  const handleAddWords = async (element: AssignedThemeElement) => {
    setSaving(true)
    try {
      const result = await lessonWordsApi.addFromThemeElement(lessonId, element.id)
      setError(null)
      onWordsAdded?.()
      // Всё уже было в уроке — иначе учитель нажимает и не понимает, сработало ли
      if (result.created.length === 0) setNotice(t('programs.lessonBlock.wordsAlreadyInLesson'))
      else setNotice(t('programs.lessonBlock.wordsAdded', { count: result.created.length }))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  if (loading && payload === null) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Skeleton variant="text" width="35%" height={28} />
          <Skeleton variant="rectangular" height={72} sx={{ mt: 1, borderRadius: 1 }} />
        </CardContent>
      </Card>
    )
  }

  if (failed) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6">{t('programs.lessonBlock.title', 'Программа')}</Typography>
          <Alert
            severity="error"
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={() => void load()}>
                {t('common.retry', 'Повторить')}
              </Button>
            }
          >
            {error || t('common.error')}
          </Alert>
        </CardContent>
      </Card>
    )
  }

  // themes — набор урока; theme оставлен сервером для совместимости, здесь он
  // нужен только как запасной вариант, если клиент старше ответа
  const themes = payload?.themes ?? (payload?.theme ? [payload.theme] : [])
  const availableThemes = payload?.available_themes ?? []

  // Порядок прохождения, без уже добавленных: повторно ту же тему класть некуда
  const pickable = availableThemes
    .filter((option) => !themes.some((item) => item.id === option.id))
    .slice()
    .sort((a, b) => a.position - b.position)

  // Группировка по разделам в порядке их первой темы — тот же вывод, что в
  // конструкторе программы: собственной позиции у раздела нет
  const pickableGroups = pickable.reduce<Array<{ title: string; themes: typeof pickable }>>((groups, option) => {
    const title = option.section_title ?? t('programs.noSection')
    const group = groups.find((item) => item.title === title)

    if (group) group.themes.push(option)
    else groups.push({ title, themes: [option] })

    return groups
  }, [])

  // Программы у урока нет — блок не занимает место на странице
  if (!payload || (themes.length === 0 && availableThemes.length === 0)) {
    return null
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6">{t('programs.lessonBlock.title', 'Программа')}</Typography>
          {/* Провалиться из урока в полную карточку программы: учитель ведёт
              занятие и хочет увидеть, что у ученика с программой целиком */}
          {payload.assignment_id != null && (
            <Link
              component={RouterLink}
              to={ROUTES.PROGRAM_ASSIGNMENT(payload.assignment_id)}
              variant="body2"
              sx={{ ml: 'auto' }}
            >
              {t('programs.lessonBlock.openAssignment')}
            </Link>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {notice && (
          <Alert severity="success" sx={{ mt: 1 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {themes.length > 0 ? (
          themes.map((item, index) => {
            const hidden = item.hidden === true || item.title === null
            // Номер по позиции темы в программе: место в служебном списке
            // available_themes расходилось с номером в статистике и на дашборде
            const number = item.position + 1

            return (
              <Box key={item.id} sx={{ mt: index === 0 ? 1 : 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                  {number > 0 && (
                    <Typography variant="h6" color="text.secondary" component="span">
                      {t('programs.themeNumber', 'Тема {{n}}', { n: number })}
                    </Typography>
                  )}
                  <Typography variant="h6" fontWeight={700}>
                    {hidden ? t('programs.my.hiddenTheme', 'Тема откроется позже') : item.title}
                  </Typography>
                  {payload.is_editable && (
                    <Button
                      size="small"
                      color="inherit"
                      disabled={saving}
                      sx={{ ml: 'auto' }}
                      onClick={() => void handleRemoveTheme(item.id)}
                    >
                      {t('programs.lessonBlock.detach', 'Снять тему')}
                    </Button>
                  )}
                </Box>

                {item.summary && (
                  <Typography variant="body2" color="text.secondary">
                    {item.summary}
                  </Typography>
                )}

                {(item.elements?.length ?? 0) > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <ThemeElementList
                      elements={item.elements ?? []}
                      // Отмечать блоки пройденными может только учитель урока —
                      // тем же правом, что и менять тему.
                      onToggleComplete={payload.is_editable ? handleToggleElement : undefined}
                      onAddWordsToLesson={payload.is_editable ? handleAddWords : undefined}
                      defaultExpanded
                      disabled={saving}
                    />
                    <LessonExercisePanel
                      lessonId={lessonId}
                      elements={item.elements ?? []}
                      isTeacher={isLessonTeacher || payload.is_editable}
                      refreshToken={refreshToken}
                    />
                  </>
                )}
              </Box>
            )
          })
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('programs.lessonBlock.noTheme', 'Тема на этот урок не выбрана')}
            </Typography>
            {/* Панель работ нужна и без темы: задание выдают из банка, а не
                только из программы, и урок без темы — обычное дело. Без неё
                кнопка выдачи была недостижима, а уже выданные работы не
                показывались вовсе. */}
            <LessonExercisePanel
              lessonId={lessonId}
              elements={[]}
              isTeacher={isLessonTeacher || payload.is_editable}
              refreshToken={refreshToken}
            />
          </>
        )}

        {payload.is_editable && availableThemes.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2, alignItems: 'flex-start' }}>
            {/* Контурный блок под темами, а не селект в подвале: добавление
                темы — такое же действие, как добавление раздела в конструкторе */}
            <Card
              variant="outlined"
              onClick={() => {
                if (!saving) setPickerOpen(true)
              }}
              sx={{
                width: '100%',
                borderStyle: 'dashed',
                cursor: saving ? 'default' : 'pointer',
                bgcolor: 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 2 }}>
                <AddIcon fontSize="small" color="action" />
                <Typography color="text.secondary">{t('programs.lessonBlock.addTheme')}</Typography>
              </CardContent>
            </Card>

            {themes.length > 1 && (
              <Button size="small" disabled={saving} onClick={handleDetach}>
                {t('programs.lessonBlock.detachAll')}
              </Button>
            )}
          </Box>
        )}
      </CardContent>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('programs.lessonBlock.addTheme')}</DialogTitle>
        <DialogContent dividers>
          {pickable.length === 0 ? (
            <Typography color="text.secondary">{t('programs.lessonBlock.allThemesAdded')}</Typography>
          ) : (
            <List disablePadding>
              {pickableGroups.map((group) => (
                <Box key={group.title}>
                  {/* Раздел заголовком: тема без него теряет место в программе —
                      «Public Transport» из какого юнита? */}
                  <ListSubheader disableSticky sx={{ bgcolor: 'transparent', lineHeight: '32px' }}>
                    {group.title}
                  </ListSubheader>
                  {group.themes.map((option) => (
                    <ListItemButton
                      key={option.id}
                      disabled={saving}
                      onClick={() => {
                        setPickerOpen(false)
                        void handleAddTheme(option.id)
                      }}
                    >
                      <ListItemText
                        primary={`${option.position + 1}. ${option.title ?? ''}`}
                        secondary={
                          option.status === 'done'
                            ? t('programs.status.done', 'пройдена')
                            : option.status === 'in_progress'
                              ? t('programs.status.inProgress')
                              : t('programs.status.planned')
                        }
                      />
                    </ListItemButton>
                  ))}
                </Box>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
